import { IRestaurant, CoordinatesPolygon } from '../types/restaurant';

export interface RestaurantDataResponse {
  restaurant: IRestaurant;
}

/**
 * Load restaurant data by restaurant ID
 * @param restaurantId - The restaurant identifier (e.g., 'wl', 'sl')
 * @returns Promise<RestaurantDataResponse> - The restaurant data response
 */
export const loadRestaurantData = async (restaurantId: string): Promise<RestaurantDataResponse> => {
  try {
    const response = await fetch(`/data/restaurants/${restaurantId}.json`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Restaurant '${restaurantId}' not found`);
      }
      throw new Error(`Failed to load restaurant data: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Validate that the response has the expected structure
    if (!data || !data.restaurant) {
      throw new Error(`Invalid restaurant data format for '${restaurantId}'`);
    }

    const normalizedRestaurant = normalizeRestaurant(data.restaurant);

    return {
      ...data,
      restaurant: normalizedRestaurant,
    };
  } catch (error) {
    console.error(`Error loading restaurant data for '${restaurantId}':`, error);
    throw error;
  }
};

/**
 * Get list of available restaurant shortcodes
 * This is a simple implementation that could be expanded to read from a config file
 * @returns string[] - Array of available restaurant shortcodes
 */
export const getAvailableRestaurants = (): string[] => {
  // For now, return hardcoded list. In the future, this could read from a config file
  return ['wl', 'sl'];
};

/**
 * Validate if a restaurant shortcode exists
 * @param shortcode - The restaurant shortcode (e.g., 'wl', 'sl')
 * @returns boolean - Whether the restaurant exists
 */
export const validateRestaurantExists = (shortcode: string): boolean => {
  const availableRestaurants = getAvailableRestaurants();
  return availableRestaurants.includes(shortcode);
};

const normalizeRestaurant = (restaurant: IRestaurant): IRestaurant => {
  let normalizedLocation: IRestaurant['location'];
  if (restaurant.location && Array.isArray(restaurant.location.coordinates)) {
    normalizedLocation = {
      ...restaurant.location,
      coordinates: (restaurant.location.coordinates as Array<number | string>).map(coord => Number(coord)) as [number, number],
    };
  } else {
    normalizedLocation = restaurant.location ?? undefined;
  }

  const normalizedBounds = normalizeDeliveryBounds(restaurant.deliveryBounds);

  return {
    ...restaurant,
    location: normalizedLocation,
    deliveryBounds: normalizedBounds,
  };
};

const normalizeDeliveryBounds = (bounds: unknown): CoordinatesPolygon | undefined => {
  if (!bounds) {
    return undefined;
  }

  const rings = Array.isArray(bounds)
    ? bounds
    : Array.isArray((bounds as { coordinates?: unknown }).coordinates)
    ? (bounds as { coordinates: unknown }).coordinates
    : undefined;

  if (!Array.isArray(rings)) {
    return undefined;
  }

  const normalized = rings
    .filter(ring => Array.isArray(ring))
    .map(ring =>
      (ring as Array<[number, number]>)
        .filter(point => Array.isArray(point) && point.length >= 2)
        .map(([lon, lat]) => [Number(lon), Number(lat)] as [number, number])
    )
    .filter(ring => ring.length > 0);

  return normalized.length > 0 ? normalized : undefined;
};
